import React from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POST_COUNT = 4;

/**
 * Official Instagram embed card.
 *
 * Renders Instagram's /p/{shortcode}/embed/ iframe. The viewport clips
 * the bottom (action bar, caption, timestamp) via overflow:hidden,
 * showing ONLY: header (avatar + @username) + media.
 */
const InstagramEmbedCard = ({ shortcode, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: index * 0.08 }}
    className="ig-embed-card group"
    data-testid={`instagram-card-${index}`}
  >
    <div className="ig-embed-viewport">
      <iframe
        src={`https://www.instagram.com/p/${shortcode}/embed/?cr=1&v=14&wp=540&rd=https%3A%2F%2Feliabejeans.com`}
        title={`Instagram @${BRAND.instagram} — ${shortcode}`}
        className="ig-embed-iframe"
        scrolling="no"
        frameBorder="0"
        loading="lazy"
        allow="encrypted-media"
        data-testid={`instagram-iframe-${index}`}
      />
    </div>
  </motion.div>
);

/**
 * Loading skeleton — 4 shimmer placeholders while posts are fetched.
 */
const LoadingSkeleton = () => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
    data-testid="instagram-grid"
  >
    {Array.from({ length: POST_COUNT }).map((_, i) => (
      <div
        key={`skel-${i}`}
        className="bg-white rounded-md overflow-hidden shadow-lg animate-pulse"
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E0]">
          <div className="w-9 h-9 rounded-full bg-gray-200" />
          <div className="h-3 w-24 rounded bg-gray-200" />
        </div>
        <div className="aspect-square bg-gray-200" />
      </div>
    ))}
  </div>
);

/**
 * Graceful fallback shown when the Instagram feed is unavailable (feature
 * disabled, credentials missing or upstream error). It never shows an error
 * and never keeps a skeleton on screen forever.
 */
const FollowBlock = () => {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col items-center justify-center text-center py-8"
      data-testid="instagram-follow-block"
    >
      <Instagram size={32} strokeWidth={1.5} className="mb-5 text-white/70" />
      <p className="font-body text-white/80 text-sm max-w-md mb-7">
        {t("instagram.fallbackMessage")}
      </p>
      <a
        href={BRAND.instagramUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="instagram-follow-link"
        className="inline-flex items-center gap-2 bg-white text-[#2A3563] font-body font-semibold px-5 py-3 rounded-sm hover:bg-[#F4B183] hover:text-[#1A1A1A] transition-colors"
      >
        <Instagram size={18} /> {t("instagram.followButton", { handle: BRAND.instagram })}
      </a>
    </div>
  );
};

export const InstagramSection = () => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["instagram-latest", BRAND.instagram, POST_COUNT],
    queryFn: async () => {
      const res = await fetch(
        `${API}/instagram/latest?username=${BRAND.instagram}&limit=${POST_COUNT}`
      );
      if (!res.ok) throw new Error("Failed to fetch Instagram posts");
      return res.json();
    },
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
    retry: false, // single effective request per page load
    refetchOnWindowFocus: false,
  });

  // Response envelope: { posts, status: "ok" | "stale" | "unavailable", cachedAt }
  const posts = Array.isArray(data?.posts) ? data.posts : [];
  const status = isLoading ? "loading" : data?.status || "unavailable";
  const shortcodes = posts.map((p) => p.shortcode).filter(Boolean);
  const canShowGrid =
    !isLoading && status !== "unavailable" && shortcodes.length > 0;

  const renderContent = () => {
    if (isLoading) return <LoadingSkeleton />;

    if (canShowGrid) {
      return (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6"
          data-testid="instagram-grid"
        >
          {shortcodes.slice(0, POST_COUNT).map((shortcode, i) => (
            <InstagramEmbedCard
              key={`ig-${shortcode}-${i}`}
              shortcode={shortcode}
              index={i}
            />
          ))}
        </div>
      );
    }

    return <FollowBlock />;
  };

  return (
    <section
      data-testid="instagram-section"
      className="bg-[#2A3563] text-white py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <span className="font-body uppercase tracking-[0.28em] text-[11px] sm:text-xs text-[#F4B183] font-semibold">
              {t("instagram.eyebrow")}
            </span>
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl md:text-5xl mt-3 md:mt-4 leading-tight">
              <Trans
                i18nKey="instagram.title"
                components={{
                  em: (
                    <span className="font-serif-editorial italic font-normal" />
                  ),
                }}
              />
            </h2>
            <p
              className="font-body text-white/70 text-sm mt-3 max-w-md"
              data-testid="instagram-subtitle"
            >
              {t("instagram.subtitle", { handle: BRAND.instagram })}
            </p>
          </div>
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-btn"
            className="inline-flex items-center gap-2 bg-white text-[#2A3563] font-body font-semibold px-5 py-3 rounded-sm hover:bg-[#F4B183] hover:text-[#1A1A1A] transition-colors self-start md:self-auto"
          >
            <Instagram size={18} />{" "}
            {t("instagram.followButton", { handle: BRAND.instagram })}
          </a>
        </div>

        {renderContent()}
      </div>
    </section>
  );
};